import { Link } from 'react-router-dom';
import { MarketButtons } from '../components/MarketButtons';
import { useState, useEffect, useCallback } from 'react';
import { BrandNav, SiteFooter, ContactCTA } from '../components/SiteChrome';
import {
  Globe,
  Loader2,
  Plus,
  X,
  ArrowUpRight,
  Shield,
  Activity,
  Target,
  Gauge,
  Wind,
  Zap,
  AlertTriangle,
  Info,
  ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_COLORS = {
  'Constructive': 'text-emerald-400',
  'Building Strength': 'text-emerald-400',
  'Building': 'text-emerald-400',
  'Neutral': 'text-zinc-300',
  'Mixed': 'text-yellow-400',
  'Fragile Rally': 'text-yellow-400',
  'Under Pressure': 'text-orange-400',
  'Extended Risk': 'text-red-400',
  'Breakdown Risk': 'text-red-500',
  'Unavailable': 'text-zinc-600',
};

const STATE_BG = {
  'Constructive': 'bg-emerald-400/5 border-emerald-400/10',
  'Building Strength': 'bg-emerald-400/5 border-emerald-400/10',
  'Building': 'bg-emerald-400/5 border-emerald-400/10',
  'Under Pressure': 'bg-orange-400/5 border-orange-400/10',
  'Breakdown Risk': 'bg-red-500/5 border-red-500/10',
  'Extended Risk': 'bg-red-400/5 border-red-400/10',
};

const LABEL_COLORS = {
  'Efficient Move': 'text-emerald-400', 'Supported Move': 'text-emerald-400',
  'Weak Move': 'text-orange-400', 'Exhausted Move': 'text-red-400',
  'Low Pressure': 'text-emerald-400', 'Building Pressure': 'text-yellow-400',
  'High Pressure': 'text-orange-400', 'Breakdown Risk': 'text-red-400',
  'Advantage': 'text-emerald-400', 'Neutral': 'text-zinc-300',
  'Fragile': 'text-orange-400', 'Extended': 'text-red-400',
  'Building': 'text-emerald-400', 'Sustained': 'text-emerald-400',
  'Stalling': 'text-yellow-400', 'Reversing': 'text-red-400',
  'Supportive': 'text-emerald-400', 'Mixed': 'text-yellow-400',
  'Headwind': 'text-red-400',
  'Low': 'text-emerald-400', 'Moderate': 'text-yellow-400',
  'Elevated': 'text-orange-400', 'High': 'text-red-400',
  'Strong': 'text-brand', 'Partial': 'text-yellow-400', 'Weak': 'text-zinc-500',
  'N/A': 'text-zinc-600', 'New': 'text-zinc-500',
};

function MarketCard({ m }) {
  const stateColor = STATE_COLORS[m.state] || 'text-zinc-400';
  const bg = STATE_BG[m.state] || 'bg-zinc-950 border-zinc-800';
  return (
    <div className={`border rounded-lg p-4 ${bg}`} data-testid={`market-card-${m.market}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono text-zinc-500">{m.market}</span>
        <span className={`text-xs font-mono ${m.change_pct != null ? (m.change_pct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-zinc-600'}`}>
          {m.change_pct != null ? `${m.change_pct >= 0 ? '+' : ''}${m.change_pct}%` : '—'}
        </span>
      </div>
      <p className={`text-sm font-bold font-mono ${stateColor}`}>{m.state}</p>
      <p className="text-[11px] text-zinc-500 mt-1.5 leading-relaxed">{m.what_mattered}</p>
      <div className="flex gap-3 mt-2.5 text-[10px] font-mono text-zinc-600">
        <span>P: <span className={LABEL_COLORS[m.pressure] || 'text-zinc-400'}>{m.pressure}</span></span>
        <span>M: <span className={LABEL_COLORS[m.momentum] || 'text-zinc-400'}>{m.momentum}</span></span>
      </div>
    </div>
  );
}

export default function MarketOverview() {
  const [markets, setMarkets] = useState([]);
  const [marketsLoading, setMarketsLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistResults, setWatchlistResults] = useState([]);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newMarket, setNewMarket] = useState('NASDAQ');

  useEffect(() => {
    fetch(`${API_URL}/api/market-overview`)
      .then(r => r.json())
      .then(data => setMarkets(data.markets || []))
      .catch(() => {})
      .finally(() => setMarketsLoading(false));
  }, []);

  const addToWatchlist = () => {
    const t = newTicker.trim().toUpperCase();
    if (!t || watchlist.length >= 10) return;
    if (watchlist.some(w => w.ticker === t && w.market === newMarket)) return;
    setWatchlist(prev => [...prev, { ticker: t, market: newMarket }]);
    setNewTicker('');
  };

  const removeFromWatchlist = (ticker) => {
    setWatchlist(prev => prev.filter(w => w.ticker !== ticker));
    setWatchlistResults(prev => prev.filter(r => r.ticker !== ticker));
  };

  const runWatchlist = useCallback(async () => {
    if (watchlist.length === 0) return;
    setWatchlistLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/watchlist-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers: watchlist }),
      });
      const data = await res.json();
      setWatchlistResults(data.results || []);
    } catch {
      // silent
    } finally {
      setWatchlistLoading(false);
    }
  }, [watchlist]);

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <BrandNav width="max-w-6xl" />

      <main className="flex-1 max-w-6xl mx-auto px-4 py-6 w-full">
        {/* === MARKET OVERVIEW === */}
        <section className="mb-8" data-testid="market-overview-section">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-brand" />
            <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">Market Overview</h2>
          </div>

          {marketsLoading && (
            <div className="flex items-center gap-2 text-zinc-500 text-sm py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading markets...
            </div>
          )}

          {!marketsLoading && markets.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {markets.map(m => <MarketCard key={m.market} m={m} />)}
            </div>
          )}

          {!marketsLoading && markets.length === 0 && (
            <p className="text-sm text-zinc-600 py-8 text-center">Market data temporarily unavailable.</p>
          )}
        </section>

        {/* === WATCHLIST === */}
        <section data-testid="watchlist-section">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-brand" />
            <h2 className="text-sm font-mono uppercase tracking-wider text-zinc-400">Watchlist Intelligence</h2>
          </div>

          {/* Add ticker */}
          <div className="flex gap-2 mb-4 flex-wrap">
            <input
              type="text"
              value={newTicker}
              onChange={e => setNewTicker(e.target.value.toUpperCase())}
              onKeyDown={e => { if (e.key === 'Enter') addToWatchlist(); }}
              placeholder="Ticker"
              className="bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-brand/50 font-mono w-28"
              maxLength={10}
              data-testid="watchlist-ticker-input"
            />
            <div className="w-full order-first"><MarketButtons value={newMarket} onChange={setNewMarket} prefix="watchlist" compact /></div>
            <button
              onClick={addToWatchlist}
              disabled={!newTicker.trim() || watchlist.length >= 10}
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm px-3 py-2 rounded-md transition-colors disabled:opacity-30 flex items-center gap-1"
              data-testid="watchlist-add-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
            {watchlist.length > 0 && (
              <button
                onClick={runWatchlist}
                disabled={watchlistLoading}
                className="bg-brand hover:bg-brand-light text-black text-sm font-bold px-4 py-2 rounded-md transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid="watchlist-run-btn"
              >
                {watchlistLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Analyse {watchlist.length} Ticker{watchlist.length !== 1 ? 's' : ''}
              </button>
            )}
          </div>

          {/* Watchlist chips */}
          {watchlist.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {watchlist.map(w => (
                <span key={w.ticker} className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-800 px-2.5 py-1 rounded text-xs font-mono text-zinc-400">
                  {w.ticker} <span className="text-zinc-600">{w.market}</span>
                  <button onClick={() => removeFromWatchlist(w.ticker)} className="text-zinc-600 hover:text-red-400 ml-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Results Table */}
          {watchlistResults.length > 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-x-auto" data-testid="watchlist-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-800">
                    {['Ticker', 'State', 'Pressure', 'Position', 'Momentum', 'Env', 'Confidence', 'Scenario', 'DNA'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left font-mono uppercase tracking-wider text-zinc-600 font-normal whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {watchlistResults.map(r => (
                    <tr key={r.ticker} className="border-b border-zinc-800/40 hover:bg-zinc-900/30 transition-colors">
                      <td className="px-3 py-2.5">
                        <Link to={`/?ticker=${r.ticker}&market=${r.market}`} className="font-mono font-medium text-white hover:text-brand transition-colors">
                          {r.ticker}
                        </Link>
                        <p className="text-[10px] text-zinc-600 mt-0.5">${r.price?.toFixed(2)}</p>
                      </td>
                      <td className={`px-3 py-2.5 font-mono font-medium whitespace-nowrap ${STATE_COLORS[r.state] || 'text-zinc-400'}`}>{r.state}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.pressure] || 'text-zinc-400'}`}>{r.pressure}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.position] || 'text-zinc-400'}`}>{r.position}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.momentum] || 'text-zinc-400'}`}>{r.momentum}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.environment] || 'text-zinc-400'}`}>{r.environment}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.confidence] || 'text-zinc-400'}`}>{r.confidence}</td>
                      <td className={`px-3 py-2.5 font-mono whitespace-nowrap ${LABEL_COLORS[r.scenario_match] || 'text-zinc-400'}`}>{r.scenario_match}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${r.dna_tag === 'New' ? 'bg-zinc-800 text-zinc-500' : 'bg-brand/10 text-brand'}`}>
                          {r.dna_tag}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Verdicts */}
              <div className="border-t border-zinc-800 p-3 space-y-2">
                <p className="text-[10px] font-mono uppercase text-zinc-600">Verdicts</p>
                {watchlistResults.filter(r => !r.error && r.verdict).map(r => (
                  <div key={r.ticker} className="flex items-start gap-2 text-xs">
                    <span className="font-mono text-zinc-500 w-12 flex-shrink-0">{r.ticker}</span>
                    <span className="text-zinc-400">{r.verdict}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {watchlist.length === 0 && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-6 text-center">
              <p className="text-sm text-zinc-500 mb-1">Add tickers to build your watchlist</p>
              <p className="text-[11px] text-zinc-600">Up to 10 tickers analysed simultaneously with full engine reads.</p>
            </div>
          )}
        </section>
        <ContactCTA className="mt-8" source="markets" />
      </main>

      <SiteFooter />
    </div>
  );
}
